import {
	h,
	Component,
	Prop,
	Listen,
	Element,
	Host,
	State,
	Method,
	Watch,
} from '@stencil/core';
import lottie, { AnimationItem } from 'lottie-web';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';
import isChromatic from 'chromatic/isChromatic';
import { calculateChildTagName } from '../_helpers/prefixes';
import { ButtonColor } from '../button/button';
import debounce from '../../../common/_helpers/debounce';

type AnimationState = 'stopped' | 'playing' | 'paused';

@Component({
	tag: 'ds-lottie-animation',
})
export class DsLottieAnimation {
	/** Element where animation is injected */
	animationContainer?: HTMLDivElement;

	debounceInitAndPlayAnimation;

	isSmall: boolean;

	/** Instance of ds-lottie-animation web component */
	@Element() animationComponent: HTMLDsLottieAnimationElement;

	/** Looping of animation option */
	@Prop() loop = true;

	/** Autoplay animation option */
	@Prop({ reflect: true }) autoplay = false;

	/** Title of animation, similar as alt on img element. Used for accessibility. */
	@Prop() imgTitle = '';

	/** Description of animated image. Used for accessibility. */
	@Prop() desc = '';

	/** Breakpoints on which animation should appear */
	@Prop() showAt: Breakpoint[] | 'all' = 'all';

	/** Path to json which describes lottie animation. Used by lottie-web */
	@Prop() animationPath: string;

	/** Path to json which describes lottie animation alternative for small breakpoints. Used by lottie-web */
	@Prop() animationAltPath: string;

	/** Alternative title of animation for small breakpoints, similar as alt on img element. Used for accessibility. */
	@Prop() imgAltTitle: string;

	/** Alternative description of animated image for small breakpoints. Used for accessibility. */
	@Prop() imgAltDesc: string;

	/* A flag for showing the play/pause button*/
	@Prop({ reflect: true }) showPlayPauseButton: false;

	/* A flag to specify the play pause button color*/
	@Prop({ reflect: true }) playPauseButtonColor: ButtonColor = 'gray';

	/* A flag to specify that the play/pause button is hidden unless hovering over the animation. Note: this can be dynamically changed by the component itself. */
	@Prop({ reflect: true }) autoHidePlayPauseButton = false;

	/* State to remember if the animation is playing/paused/stopped */
	@State() animationState: AnimationState = 'stopped';

	/** Instance of lottie animation library */
	@State() lottieAnimation: AnimationItem | null;

	/**
	 * When clicking on the animation, also click on whichever button is currently in view, i.e. the play or the pause button.
	 * This also means that if no button is visible, no interaction takes place.
	 */
	@Listen('click')
	handleClick() {
		const button: HTMLDsButtonElement =
			this.animationComponent.querySelector('ds-button');
		if (button) {
			button.click();
		}
	}

	constructor() {
		this.addAccessibility = this.addAccessibility.bind(this);
	}

	componentWillLoad() {
		if (!this.animationPath) {
			throw new Error(
				'animation-path is required attribute! Please add it to lottie animation component'
			);
		}

		if (!this.imgTitle) {
			console.log(
				'%c[DS-LIBRARY]: Accessibility is compromised in the ds-lottie-animation component because title and desc attributes are missing',
				'color: black; background: yellow; font-weight: 600; padding: 5px 0; line-height:2;'
			);
		}
	}

	componentDidLoad() {
		if (this.shouldShowAnimation()) {
			this.initAndPlayAnimation();
		}
		this.isSmall = this.checkIsSmall();
		this.debounceInitAndPlayAnimation = debounce(() => {
			this.initAndPlayAnimation();
		}, 200);
	}

	disconnectedCallback() {
		this.lottieAnimation.removeEventListener(
			'DOMLoaded',
			this.addAccessibility.bind(this)
		);
	}

	@Listen('resize', { target: 'window', passive: true })
	windowResizeHandler() {
		if (this.shouldShowAnimation()) {
			this.debounceInitAndPlayAnimation();
		}
	}

	@Watch('lottieAnimation')
	onAnimationChange(curr?: AnimationItem | null, prev?: AnimationItem | null) {
		if (curr && curr !== prev) {
			this.lottieAnimation.addEventListener('DOMLoaded', this.addAccessibility);
		}
	}

	@Method()
	async play() {
		if (this.lottieAnimation) {
			this.lottieAnimation.play();
			this.animationState = 'playing';
		}
	}

	@Method()
	async stop() {
		if (this.lottieAnimation) {
			this.lottieAnimation.stop();
			this.animationState = 'stopped';
		}
	}

	@Method()
	async pause() {
		if (this.lottieAnimation) {
			this.lottieAnimation.pause();
			this.animationState = 'paused';
		}
	}

	@Method()
	async setSpeed(speed: number) {
		if (this.lottieAnimation) {
			this.lottieAnimation.setSpeed(speed);
		}
	}

	shouldShowAnimation() {
		return (
			this.showAt.includes(getCurrentBreakpoint()) || this.showAt === 'all'
		);
	}

	initAndPlayAnimation() {
		this.resetIfDifferentScreenSize();
		this.lottieAnimation = this.lottieAnimation || this.createLottieInstance();
		if (isChromatic()) {
			return;
		}
		if (this.autoplay) {
			this.play();
			this.autoHidePlayPauseButton = true;
		} else {
			this.pause();
			this.autoHidePlayPauseButton = false;
		}
	}

	removeAnimation() {
		if (this.lottieAnimation) {
			this.lottieAnimation.removeEventListener(
				'DOMLoaded',
				this.addAccessibility
			);
			this.lottieAnimation.destroy();
			this.lottieAnimation = null;
		}
	}

	resetIfDifferentScreenSize() {
		if (this.isSmall && !this.checkIsSmall()) {
			this.removeAnimation();
			this.isSmall = false;
		}
		if (!this.isSmall && this.checkIsSmall()) {
			this.removeAnimation();
			this.isSmall = true;
		}
	}

	addAccessibility() {
		const svgAnimation = this.animationComponent?.querySelector('svg');
		if (svgAnimation) {
			const title = document.createElement('title');
			const desc = document.createElement('desc');

			title.innerText = this.checkIsSmall() ? this.imgAltTitle : this.imgTitle;
			title.id = 'imgTitle';
			desc.innerText = this.checkIsSmall() ? this.imgAltDesc : this.desc;
			desc.id = 'imgDesc';

			svgAnimation.prepend(desc);
			svgAnimation.prepend(title);

			svgAnimation.setAttribute('aria-labelledby', 'imgTitle imgDesc');
			svgAnimation.setAttribute('role', 'img');
		}
	}

	getImagePath() {
		return this.checkIsSmall()
			? this.animationAltPath
				? this.animationAltPath
				: this.animationPath
			: this.animationPath;
	}

	checkIsSmall() {
		const currentBreakpoint = getCurrentBreakpoint();
		const breakPointsForSmallImg = [Breakpoint.XS, Breakpoint.SM];
		return breakPointsForSmallImg.includes(currentBreakpoint);
	}

	getAnimationProps() {
		return {
			path: this.getImagePath(),
			loop: this.loop,
			autoplay: this.autoplay,
		};
	}

	createLottieInstance() {
		return lottie.loadAnimation({
			container: this.animationContainer,
			renderer: 'svg',
			...this.getAnimationProps(),
		});
	}

	playButtonOnClick() {
		this.play();
		this.autoHidePlayPauseButton = true;
	}

	pauseButtonOnClick() {
		this.pause();
	}

	render() {
		const PlayButton = calculateChildTagName(
			this.animationComponent,
			'ds-button'
		);
		const PauseButton = calculateChildTagName(
			this.animationComponent,
			'ds-button'
		);

		return (
			<Host>
				{[
					<div class={[this.lottieAnimation ? 'd-none' : ''].join(' ')}>
						<slot />
					</div>,
					<div
						class="ds-lottie-animation-container"
						ref={(el) => (this.animationContainer = el as HTMLDivElement)}
					></div>,
					this.showPlayPauseButton &&
						this.lottieAnimation &&
						(this.animationState === 'playing' ? (
							<PauseButton
								onClick={() => this.pauseButtonOnClick()}
								color={this.playPauseButtonColor}
							>
								<i class={`fa fa-pause`} />
							</PauseButton>
						) : (
							<PlayButton
								onClick={() => this.playButtonOnClick()}
								color={this.playPauseButtonColor}
							>
								<i class={`fa fa-play`} />
							</PlayButton>
						)),
				]}
			</Host>
		);
	}
}
